/**
 * AppSettingsModal - Configure voice app settings
 *
 * A modal dialog for editing app-wide settings:
 * - App name and description
 * - Voice agent (voice selection, greeting, personality)
 * - Theme (primary color, dark mode)
 * - Connected screens list
 *
 * Settings are stored on the home screen node and apply to all
 * connected screens in the same app.
 */

import { useState, useCallback, useEffect } from 'react';
import type { Node, Edge } from '@xyflow/react';
import type {
  AppSettings,
  AppScreenNodeData,
  ClientToolDefinition,
} from '@deep-agents/shared-types';
import {
  VOICE_OPTIONS,
  createDefaultAppSettings,
} from '@deep-agents/shared-types';
import { CollectionsTab } from '../../collections';
import { ToolsEditor } from '../../tools';

interface ConnectedScreen {
  id: string;
  title: string;
  isHome: boolean;
  /** Widgets on this screen (for tool editor dropdowns) */
  widgets?: Array<{ id: string; type: string; label: string }>;
}

/** Available tabs in the settings modal */
type SettingsTab = 'general' | 'collections' | 'tools';

interface AppSettingsModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Called when modal should close */
  onClose: () => void;
  /** Current app settings */
  settings: AppSettings;
  /** Called when settings are saved */
  onSave: (settings: AppSettings) => void;
  /** List of connected screens in this app */
  connectedScreens: ConnectedScreen[];
  /** ID of the home screen */
  homeScreenId: string;
  /** App ID for collections API (defaults to homeScreenId if not provided) */
  appId?: string;
}

export function AppSettingsModal({
  isOpen,
  onClose,
  settings,
  onSave,
  connectedScreens,
  homeScreenId,
  appId,
}: AppSettingsModalProps) {
  // Tab state
  const [activeTab, setActiveTab] = useState<SettingsTab>('general');

  // Local state for editing
  const [name, setName] = useState(settings.name);
  const [description, setDescription] = useState(settings.description || '');
  const [voiceId, setVoiceId] = useState(settings.voice.voiceId);
  const [greeting, setGreeting] = useState(settings.voice.greeting);
  const [personality, setPersonality] = useState(settings.voice.personality);
  const [agentId, setAgentId] = useState(settings.agent?.agentId || '');
  const [primaryColor, setPrimaryColor] = useState(settings.theme.primaryColor);
  const [darkMode, setDarkMode] = useState(settings.theme.darkMode);
  const [clientTools, setClientTools] = useState<ClientToolDefinition[]>(settings.agent?.clientTools || []);

  // Effective app ID for collections
  const effectiveAppId = appId || homeScreenId;

  // Reset form when settings change
  useEffect(() => {
    setName(settings.name);
    setDescription(settings.description || '');
    setVoiceId(settings.voice.voiceId);
    setGreeting(settings.voice.greeting);
    setPersonality(settings.voice.personality);
    setAgentId(settings.agent?.agentId || '');
    setPrimaryColor(settings.theme.primaryColor);
    setDarkMode(settings.theme.darkMode);
    setClientTools(settings.agent?.clientTools || []);
  }, [settings]);

  // Handle save
  const handleSave = useCallback(() => {
    const updatedSettings: AppSettings = {
      name,
      description: description || undefined,
      voice: {
        voiceId,
        greeting,
        personality,
      },
      theme: {
        primaryColor,
        darkMode,
      },
      // Include agent config if agent ID or custom tools are defined
      agent: (agentId || clientTools.length > 0)
        ? { agentId: agentId || undefined, clientTools: clientTools.length > 0 ? clientTools : undefined }
        : undefined,
    };
    onSave(updatedSettings);
    onClose();
  }, [name, description, voiceId, greeting, personality, agentId, clientTools, primaryColor, darkMode, onSave, onClose]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    // Reset to original values
    setName(settings.name);
    setDescription(settings.description || '');
    setVoiceId(settings.voice.voiceId);
    setGreeting(settings.voice.greeting);
    setPersonality(settings.voice.personality);
    setAgentId(settings.agent?.agentId || '');
    setPrimaryColor(settings.theme.primaryColor);
    setDarkMode(settings.theme.darkMode);
    setClientTools(settings.agent?.clientTools || []);
    onClose();
  }, [settings, onClose]);

  // Handle backdrop click
  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleCancel();
    }
  }, [handleCancel]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={handleBackdropClick}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Voice App Settings</h2>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none p-1"
          >
            x
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-6 pt-4 border-b border-gray-100">
          <button
            onClick={() => setActiveTab('general')}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === 'general'
                ? 'text-violet-600 bg-violet-50 border-b-2 border-violet-600'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            General
          </button>
          <button
            onClick={() => setActiveTab('collections')}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === 'collections'
                ? 'text-violet-600 bg-violet-50 border-b-2 border-violet-600'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            Collections
          </button>
          <button
            onClick={() => setActiveTab('tools')}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === 'tools'
                ? 'text-violet-600 bg-violet-50 border-b-2 border-violet-600'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            Tools
            {clientTools.length > 0 && (
              <span className="ml-1.5 text-xs bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded-full">
                {clientTools.length}
              </span>
            )}
          </button>
        </div>

        {/* Body - scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {/* Collections Tab */}
          {activeTab === 'collections' && (
            <CollectionsTab appId={effectiveAppId} />
          )}

          {/* Tools Tab */}
          {activeTab === 'tools' && (
            <ToolsEditor
              tools={clientTools}
              onChange={setClientTools}
              screens={connectedScreens}
            />
          )}

          {/* General Tab */}
          {activeTab === 'general' && (
            <>
          {/* App Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              App Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My Voice App"
              className="w-full px-3 py-2 text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="A brief description of your app"
              className="w-full px-3 py-2 text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
            />
          </div>

          {/* Voice Agent Section */}
          <div className="pt-4 border-t border-gray-100">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
              Voice Agent
            </h3>

            {/* Voice Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Voice
              </label>
              <select
                value={voiceId}
                onChange={(e) => setVoiceId(e.target.value)}
                className="w-full px-3 py-2 text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              >
                {VOICE_OPTIONS.map((voice) => (
                  <option key={voice.id} value={voice.id}>
                    {voice.name} - {voice.description}
                  </option>
                ))}
              </select>
            </div>

            {/* Greeting */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Greeting Message
              </label>
              <input
                type="text"
                value={greeting}
                onChange={(e) => setGreeting(e.target.value)}
                placeholder="Hi! How can I help you today?"
                className="w-full px-3 py-2 text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-400 mt-1">
                What the agent says when the app first opens
              </p>
            </div>

            {/* Personality */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Personality
              </label>
              <textarea
                value={personality}
                onChange={(e) => setPersonality(e.target.value)}
                placeholder="You are a helpful assistant..."
                rows={4}
                className="w-full px-3 py-2 text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none"
              />
              <p className="text-xs text-gray-400 mt-1">
                System prompt that defines the agent's personality and behavior
              </p>
            </div>

            {/* ElevenLabs Agent ID */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ElevenLabs Agent ID
              </label>
              <input
                type="text"
                value={agentId}
                onChange={(e) => setAgentId(e.target.value)}
                placeholder="e.g., abc123..."
                className="w-full px-3 py-2 text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-400 mt-1">
                Get your agent ID from elevenlabs.io/app/conversational-ai
              </p>
            </div>
          </div>

          {/* Theme Section */}
          <div className="pt-4 border-t border-gray-100">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
              Theme
            </h3>

            <div className="flex items-center gap-6">
              {/* Primary Color */}
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Primary Color
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    className="flex-1 px-3 py-2 text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent font-mono text-sm"
                  />
                </div>
              </div>

              {/* Dark Mode */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dark Mode
                </label>
                <button
                  onClick={() => setDarkMode(!darkMode)}
                  className={`
                    w-14 h-8 rounded-full transition-colors relative
                    ${darkMode ? 'bg-violet-600' : 'bg-gray-300'}
                  `}
                >
                  <span
                    className={`
                      absolute top-1 w-6 h-6 bg-white rounded-full shadow transition-transform
                      ${darkMode ? 'translate-x-7' : 'translate-x-1'}
                    `}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Screens Section */}
          <div className="pt-4 border-t border-gray-100">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
              Screens ({connectedScreens.length})
            </h3>

            <div className="space-y-2">
              {connectedScreens.map((screen) => (
                <div
                  key={screen.id}
                  className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg"
                >
                  <span
                    className={`w-2 h-2 rounded-full ${
                      screen.isHome ? 'bg-violet-500' : 'bg-gray-300'
                    }`}
                  />
                  <span className="text-sm text-gray-700">{screen.title}</span>
                  {screen.isHome && (
                    <span className="text-xs text-violet-600 font-medium">(Home)</span>
                  )}
                </div>
              ))}
            </div>
          </div>
            </>
          )}
        </div>

        {/* Footer - show for General and Tools tabs (they save via the Save button) */}
        {(activeTab === 'general' || activeTab === 'tools') && (
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 transition-colors"
          >
            Save
          </button>
        </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Helper: Find all connected AppScreen nodes
// ─────────────────────────────────────────────────────────────

/**
 * Finds all AppScreen nodes connected to the given node via edges.
 * Uses breadth-first search to find the complete app.
 */
export function findConnectedScreens(
  startNodeId: string,
  nodes: Node[],
  edges: Edge[]
): ConnectedScreen[] {
  const visited = new Set<string>();
  const queue = [startNodeId];
  const result: ConnectedScreen[] = [];

  // Get all appScreen nodes
  const appScreenNodes = nodes.filter((n) => n.type === 'appScreen');
  const appScreenIds = new Set(appScreenNodes.map((n) => n.id));

  while (queue.length > 0) {
    const nodeId = queue.shift()!;
    if (visited.has(nodeId)) continue;
    visited.add(nodeId);

    // Only process appScreen nodes
    if (!appScreenIds.has(nodeId)) continue;

    const node = appScreenNodes.find((n) => n.id === nodeId);
    if (!node) continue;

    const data = node.data as AppScreenNodeData;

    // Extract widget info for tool editor dropdowns
    const widgets = (data.widgets || []).map((w) => {
      // Build a human-readable label from the widget's content
      let label: string = w.type;
      const p = w.props as Record<string, unknown>;
      if (p.label) label = String(p.label);
      else if (p.title) label = String(p.title);
      else if (p.content) label = String(p.content).slice(0, 30);
      return { id: w.id, type: w.type as string, label };
    });

    result.push({
      id: node.id,
      title: data.title || 'Untitled Screen',
      isHome: data.isHomeScreen || false,
      widgets,
    });

    // Find connected nodes via edges
    for (const edge of edges) {
      if (edge.source === nodeId && appScreenIds.has(edge.target)) {
        queue.push(edge.target);
      }
      if (edge.target === nodeId && appScreenIds.has(edge.source)) {
        queue.push(edge.source);
      }
    }
  }

  // Sort: home screen first, then alphabetically
  return result.sort((a, b) => {
    if (a.isHome && !b.isHome) return -1;
    if (!a.isHome && b.isHome) return 1;
    return a.title.localeCompare(b.title);
  });
}

/**
 * Finds the home screen among connected screens
 */
export function findHomeScreen(
  startNodeId: string,
  nodes: Node[],
  edges: Edge[]
): Node | null {
  const connectedScreens = findConnectedScreens(startNodeId, nodes, edges);
  const homeScreen = connectedScreens.find((s) => s.isHome);
  if (!homeScreen) return null;
  return nodes.find((n) => n.id === homeScreen.id) || null;
}
